import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import {
  Heart,
  MessageCircle,
  MoreHorizontal,
  Share2,
  Flag,
  Trash2,
  Send,
  UserPlus,
  UserCheck,
  FileText,
  Loader2,
  CornerDownRight,
  X,
} from "lucide-react";
import {
  likeAPost,
  unlikeAPost,
  commentAPost,
  getPostComments,
  deletePost,
  followUserPost,
  unfollowUserPost,
} from "reducer/postsSlice";
import Linkify from "linkify-react";
import { getTimeAgo, serverFiles } from "utils/utils";
import {
  isCreatorBriefPost,
  mapPostToOpenBrief,
} from "utils/creatorBriefs";
import PresenceDot from "components/feed/PresenceDot";
import OpenBriefCard from "components/feed/OpenBriefCard";
import FeedMediaCarousel, {
  splitFeedMedia,
} from "components/feed/FeedMediaCarousel";
import toast from "components/Toast/toast";

const LINKIFY_OPTS = {
  target: "_blank",
  rel: "noopener noreferrer",
  className:
    "text-[#8b83ff] underline underline-offset-2 break-all hover:text-[#c9c5ff]",
};
import ReportModal from "components/UI/ReportModal/ReportModal";
import NO_IMAGE from "images/NO_IMAGE.jpg";

const initials = (name) =>
  (name || "Q")
    .split(" ")
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const Avatar = ({ src, name, size = 44, to, online }) => {
  const img = src ? (
    <img
      src={src}
      alt={name}
      style={{ height: size, width: size }}
      className="rounded-full object-cover shrink-0"
      onError={(e) => {
        e.target.onerror = null;
        e.target.src = NO_IMAGE;
      }}
    />
  ) : (
    <div
      style={{ height: size, width: size }}
      className="avatar-fallback rounded-full shrink-0 flex items-center justify-center font-semibold text-sm"
    >
      {initials(name)}
    </div>
  );
  const wrap = (
    <span className="relative inline-flex shrink-0">
      {img}
      <PresenceDot
        online={!!online}
        size={Math.max(8, Math.round(size / 4.5))}
        className="absolute bottom-0 right-0 border-2 border-zinc-900"
      />
    </span>
  );
  return to ? <Link to={to}>{wrap}</Link> : wrap;
};

const FeedChip = ({ variant = "tag", children }) =>
  children ? (
    <span className={`feed-chip feed-chip-${variant}`}>{children}</span>
  ) : null;

const commentDisplayName = (c) => {
  const owner = c?.postOwner || c;
  if (owner?.userType === "BUSINESS" || owner?.companyName) {
    return (
      owner?.companyName ||
      `${owner?.firstName || ""} ${owner?.lastName || ""}`.trim() ||
      "User"
    );
  }
  return (
    `${owner?.firstName || ""} ${owner?.lastName || ""}`.trim() ||
    c?.userName ||
    "User"
  );
};

const commentAvatar = (c) => {
  const url = c?.postOwner?.profileUrl || c?.profileUrl;
  return url ? serverFiles(url) : null;
};

const commentProfileTo = (c, myAlias) => {
  const alias = c?.postOwner?.userAliasId || c?.userAliasId;
  if (!alias) return undefined;
  return alias === myAlias ? "/user-profile" : `/user-profile/${alias}`;
};

const FeedComment = ({
  comment,
  depth = 0,
  rootId,
  myAlias,
  replyToId,
  onReply,
  busy,
}) => {
  const name = commentDisplayName(comment);
  const avatar = commentAvatar(comment);
  const to = commentProfileTo(comment, myAlias);
  const replies = Array.isArray(comment?.reply) ? comment.reply : [];
  const parentForReply = rootId || comment?.id;
  const isReplying = replyToId === comment?.id;

  return (
    <div
      className={
        depth > 0 ? "mt-2 ml-2 sm:ml-4 pl-2 sm:pl-3 border-l border-zinc-800" : ""
      }
    >
      <div className="flex items-start gap-2 sm:gap-3 pt-3">
        <Avatar src={avatar} name={name} size={depth > 0 ? 28 : 34} to={to} />
        <div className="flex-1 min-w-0">
          <div className="rounded-2xl bg-zinc-900/70 px-3 sm:px-4 py-2.5">
            <div className="flex items-baseline gap-2 flex-wrap">
              {to ? (
                <Link
                  to={to}
                  className="text-sm font-medium text-zinc-100 hover:text-[#c9c5ff]"
                >
                  {name}
                </Link>
              ) : (
                <div className="text-sm font-medium text-zinc-100">{name}</div>
              )}
              {comment?.createdAt && (
                <span className="text-[0.7rem] text-zinc-500">
                  {getTimeAgo(comment.createdAt)}
                </span>
              )}
            </div>
            <div className="text-sm text-zinc-300 whitespace-pre-wrap mt-0.5">
              <Linkify options={LINKIFY_OPTS}>
                {comment?.comment || comment?.content}
              </Linkify>
            </div>
          </div>
          <div className="mt-1 flex items-center gap-3 px-1">
            <button
              type="button"
              disabled={busy}
              onClick={() => onReply(comment, parentForReply)}
              className={`text-xs font-medium transition-colors disabled:opacity-50 ${
                isReplying
                  ? "text-[#8b83ff]"
                  : "text-zinc-500 hover:text-zinc-200"
              }`}
            >
              {isReplying ? "Replying…" : "Reply"}
            </button>
            {replies.length > 0 && (
              <span className="text-xs text-zinc-600">
                {replies.length} {replies.length === 1 ? "reply" : "replies"}
              </span>
            )}
          </div>
        </div>
      </div>
      {replies.map((r) => (
        <FeedComment
          key={r?.id}
          comment={r}
          depth={depth + 1}
          rootId={parentForReply}
          myAlias={myAlias}
          replyToId={replyToId}
          onReply={onReply}
          busy={busy}
        />
      ))}
    </div>
  );
};

const FeedPostCard = ({ post, asBriefCard = true }) => {
  if (asBriefCard && isCreatorBriefPost(post)) {
    const brief = mapPostToOpenBrief(post);
    if (brief) return <OpenBriefCard project={brief} />;
  }

  return <FeedPostCardInner post={post} />;
};

const FeedPostCardInner = ({ post }) => {
  const dispatch = useDispatch();
  const { userProfile } = useSelector((state) => state.userProfile);
  const [openComments, setOpenComments] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [replyToId, setReplyToId] = useState(null);
  const [replyTargetId, setReplyTargetId] = useState(null);
  const [replyToName, setReplyToName] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [busyComment, setBusyComment] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const menuRef = useRef(null);
  const commentLock = useRef(false);
  const followLock = useRef(false);
  const likeLock = useRef(false);

  const myAlias =
    userProfile?.userAliasId || userProfile?.companyResponseDto?.companyAliasId;
  const owner = post?.postOwner || {};
  const isBrand = owner?.userType === "BUSINESS" || !!owner?.companyName;
  const displayName = isBrand
    ? owner?.companyName ||
      `${owner?.firstName || ""} ${owner?.lastName || ""}`.trim()
    : `${owner?.firstName || ""} ${owner?.lastName || ""}`.trim() || "User";
  const avatarSrc = owner?.profileUrl ? serverFiles(owner.profileUrl) : null;
  const profileTo =
    post?.userAliasId === myAlias
      ? "/user-profile"
      : `/user-profile/${post?.userAliasId}`;
  const isOwner = post?.userAliasId === myAlias;
  const canFollow = !isOwner;
  const tag = post?.tags?.[0] && post.tags[0] !== "" ? post.tags[0] : null;
  const postURL = `${window.location.origin}/posts/${post?.id}`;

  useEffect(() => {
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    if (menuOpen) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [menuOpen]);

  const toggleLike = () => {
    if (likeLock.current) return;
    likeLock.current = true;
    try {
      if (post?.isLiked) dispatch(unlikeAPost(post.id));
      else dispatch(likeAPost(post.id));
    } finally {
      window.setTimeout(() => {
        likeLock.current = false;
      }, 400);
    }
  };

  const toggleFollow = async () => {
    if (followLock.current || followBusy) return;
    const aliasId = post?.userAliasId || owner?.userAliasId;
    if (!aliasId) {
      toast.error("Could not update follow");
      return;
    }
    followLock.current = true;
    setFollowBusy(true);
    const payload = { userAliasId: aliasId };
    try {
      if (post?.isFollowed) {
        await dispatch(unfollowUserPost({ payload, postId: post.id })).unwrap();
      } else {
        await dispatch(followUserPost({ payload, postId: post.id })).unwrap();
      }
    } catch (_) {
      toast.error("Could not update follow");
    } finally {
      setFollowBusy(false);
      window.setTimeout(() => {
        followLock.current = false;
      }, 500);
    }
  };

  const handleOpenComments = async () => {
    const next = !openComments;
    setOpenComments(next);
    if (!next) {
      setReplyToId(null);
      setReplyTargetId(null);
      setReplyToName("");
      setCommentText("");
    }
    if (next && !post?.comments) {
      await dispatch(getPostComments({ id: post.id }));
    }
  };

  const startReply = (comment, rootParentId) => {
    if (busyComment) return;
    const parentId = rootParentId || comment?.id;
    const targetId = comment?.id;
    if (!parentId || !targetId) return;
    if (replyTargetId === targetId) {
      setReplyToId(null);
      setReplyTargetId(null);
      setReplyToName("");
      return;
    }
    setReplyToId(parentId);
    setReplyTargetId(targetId);
    setReplyToName(commentDisplayName(comment));
  };

  const cancelReply = () => {
    setReplyToId(null);
    setReplyTargetId(null);
    setReplyToName("");
  };

  const submitComment = async (e) => {
    e.preventDefault();
    const text = commentText.trim();
    if (!text || busyComment || commentLock.current) return;
    commentLock.current = true;
    setBusyComment(true);
    const parentId = replyToId || undefined;
    try {
      await dispatch(
        commentAPost({
          id: post.id,
          comment: text,
          ...(parentId ? { parentId } : {}),
        })
      ).unwrap();
      setCommentText("");
      setReplyToId(null);
      setReplyTargetId(null);
      setReplyToName("");
      await dispatch(getPostComments({ id: post.id })).unwrap();
    } catch (_) {
      toast.error(parentId ? "Could not post reply" : "Could not post comment");
    } finally {
      setBusyComment(false);
      window.setTimeout(() => {
        commentLock.current = false;
      }, 400);
    }
  };

  const share = async () => {
    try {
      await navigator.clipboard.writeText(postURL);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy link");
    }
    setMenuOpen(false);
  };

  const medias = post?.medias || [];
  const { visual, files } = splitFeedMedia(medias);

  return (
    <motion.article
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35 }}
      className="rounded-2xl border border-zinc-800 bg-zinc-900/40 overflow-x-clip"
    >
      <div className="p-4 sm:p-5">
        <div className="flex items-start gap-2.5 sm:gap-3">
          <Avatar
            src={avatarSrc}
            name={displayName}
            to={profileTo}
            size={40}
            online={post?.postOwner?.isOnline}
          />
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <Link
                to={profileTo}
                className="font-display font-semibold text-sm sm:text-base text-zinc-50 tracking-tight hover:text-[#c9c5ff] truncate max-w-[min(100%,12rem)] sm:max-w-[min(100%,18rem)]"
              >
                {displayName}
              </Link>
              {canFollow && (
                <button
                  type="button"
                  onClick={toggleFollow}
                  disabled={followBusy}
                  className={`inline-flex items-center gap-1 rounded-full text-[0.7rem] sm:text-xs font-semibold transition-colors px-2 sm:px-2.5 py-1 border disabled:opacity-60 shrink-0 ${
                    post?.isFollowed
                      ? "border-zinc-700 bg-zinc-900/60 text-zinc-300 hover:bg-zinc-800"
                      : "border-[#6C63F5] bg-[#6C63F5] text-white hover:brightness-110"
                  }`}
                >
                  {post?.isFollowed ? (
                    <>
                      <UserCheck size={12} />
                      <span className="hidden sm:inline">Following</span>
                    </>
                  ) : (
                    <>
                      <UserPlus size={12} /> Follow
                    </>
                  )}
                </button>
              )}
              <span className="text-xs text-zinc-500 shrink-0 ml-auto">
                {getTimeAgo(post?.createdAt)}
              </span>
              <div ref={menuRef} className="relative shrink-0">
                <button
                  type="button"
                  onClick={() => setMenuOpen((v) => !v)}
                  aria-label="Post actions"
                  className="text-zinc-500 hover:text-zinc-100 h-8 w-8 rounded-full inline-flex items-center justify-center hover:bg-zinc-800/60 transition-colors"
                >
                  <MoreHorizontal size={18} />
                </button>
                <AnimatePresence>
                  {menuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -4, scale: 0.98 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -4, scale: 0.98 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 top-full mt-1.5 z-30 w-44 rounded-xl border border-zinc-800 bg-zinc-950/95 backdrop-blur-lg shadow-xl overflow-hidden"
                    >
                      <button
                        type="button"
                        onClick={share}
                        className="w-full text-left flex items-center gap-2.5 px-3.5 py-2 text-sm text-zinc-200 hover:bg-zinc-800"
                      >
                        <Share2 size={15} className="text-[#8b83ff]" /> Share post
                      </button>
                      {!isOwner && (
                        <button
                          type="button"
                          onClick={() => {
                            setMenuOpen(false);
                            setReportOpen(true);
                          }}
                          className="w-full text-left flex items-center gap-2.5 px-3.5 py-2 text-sm text-zinc-200 hover:bg-zinc-800"
                        >
                          <Flag size={15} className="text-amber-400" /> Report
                        </button>
                      )}
                      {isOwner && (
                        <button
                          type="button"
                          onClick={() => {
                            setMenuOpen(false);
                            dispatch(deletePost(post.id));
                          }}
                          className="w-full text-left flex items-center gap-2.5 px-3.5 py-2 text-sm text-red-300 hover:bg-zinc-800"
                        >
                          <Trash2 size={15} /> Delete
                        </button>
                      )}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
            <div className="mt-1.5 flex items-center gap-1.5">
              <FeedChip variant={isBrand ? "brand" : "creator"}>
                {isBrand ? "Brand" : "Creative"}
              </FeedChip>
              {tag ? <FeedChip variant="tag">{tag}</FeedChip> : null}
            </div>
          </div>
        </div>

        {post?.content && (
          <p className="mt-4 text-[0.95rem] text-zinc-300 leading-relaxed whitespace-pre-wrap">
            <Linkify options={LINKIFY_OPTS}>{post.content}</Linkify>
          </p>
        )}
      </div>

      {(visual.length > 0 || files.length > 0) && (
        <div className="px-4 sm:px-5 pb-4 space-y-3">
          {visual.length > 0 ? <FeedMediaCarousel items={visual} /> : null}
          {files.map((f) => (
            <a
              key={f.key}
              href={f.url}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-3 rounded-xl border border-zinc-800 px-4 py-4 hover:bg-zinc-900/60 transition-colors"
            >
              <div className="h-10 w-10 rounded-lg bg-[#6C63F5]/15 flex items-center justify-center text-[#8b83ff] shrink-0">
                <FileText size={18} />
              </div>
              <div className="min-w-0">
                <div className="text-sm text-zinc-100 truncate">
                  {f.name || "Attachment"}
                </div>
                <div className="text-xs text-zinc-500">Click to open</div>
              </div>
            </a>
          ))}
        </div>
      )}

      <div className="px-4 sm:px-5 py-3 border-t border-zinc-800/70 flex items-center gap-5 sm:gap-6 text-zinc-400">
        <button
          type="button"
          onClick={toggleLike}
          className={`inline-flex items-center gap-2 text-sm transition-colors ${
            post?.isLiked ? "text-[#6C63F5]" : "hover:text-zinc-100"
          }`}
        >
          <Heart size={18} fill={post?.isLiked ? "#6C63F5" : "none"} />{" "}
          {post?.likeCount || 0}
        </button>
        <button
          type="button"
          onClick={handleOpenComments}
          className="inline-flex items-center gap-2 text-sm hover:text-zinc-100 transition-colors"
        >
          <MessageCircle size={18} /> {post?.commentCount || 0}
        </button>
      </div>

      {openComments && (
        <div className="px-4 sm:px-5 pb-5 pt-1 border-t border-zinc-800/70 space-y-1">
          {!post?.comments ? (
            <div className="py-3 text-sm text-zinc-500 flex items-center gap-2">
              <Loader2 size={14} className="animate-spin" /> Loading…
            </div>
          ) : post.comments.length === 0 ? (
            <div className="py-2 text-sm text-zinc-500">
              Be the first to comment.
            </div>
          ) : (
            post.comments.map((c) => (
              <FeedComment
                key={c?.id}
                comment={c}
                rootId={c?.id}
                myAlias={myAlias}
                replyToId={replyTargetId}
                onReply={startReply}
                busy={busyComment}
              />
            ))
          )}

          <form onSubmit={submitComment} className="pt-3 space-y-2">
            {replyToId && (
              <div className="flex items-center gap-2 text-xs text-[#8b83ff] px-1">
                <CornerDownRight size={12} />
                <span className="truncate">
                  Replying to <span className="font-semibold">{replyToName}</span>
                </span>
                <button
                  type="button"
                  onClick={cancelReply}
                  disabled={busyComment}
                  className="ml-auto inline-flex items-center gap-1 text-zinc-500 hover:text-zinc-200 disabled:opacity-50"
                >
                  <X size={12} /> Cancel
                </button>
              </div>
            )}
            <div className="flex items-center gap-2">
              <input
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                placeholder={
                  replyToId
                    ? `Reply to ${replyToName}…`
                    : "Write a comment…"
                }
                disabled={busyComment}
                className="flex-1 rounded-full bg-zinc-900/70 border border-zinc-800 px-4 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:border-[#6C63F5] disabled:opacity-60"
              />
              <button
                type="submit"
                disabled={busyComment || !commentText.trim()}
                className="h-9 w-9 rounded-full bg-[#6C63F5] text-white flex items-center justify-center hover:bg-[#5751E5] transition-colors disabled:opacity-60"
              >
                {busyComment ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <Send size={15} />
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {reportOpen && (
        <ReportModal
          id={post?.id}
          type="Post"
          reportModal={reportOpen}
          setReportModal={setReportOpen}
        />
      )}
    </motion.article>
  );
};

export default FeedPostCard;
